/** ***********************************************************************
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ************************************************************************* */

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import uuid from 'uuid';
import { Tag } from '@react/react-spectrum/TagList';
import { PDPAnalytics } from '../../../../analytics';
import { PDP_SECTION, PDP_ACTION } from '../../../../utils/pdpUtils/pdpEnums';
import TranslationUtil from '../../../../utils/translation';

class Categories extends Component {
    onLabelClick(e, category) {
        const { PDPDataObject: { actionHandlerCallback } } = this.props;
        const { sectionId } = this.sectionData;
        const guid = uuid.v4();

        PDPAnalytics.categoryClick(category, guid, sectionId);
        const actionParam = {
            actionObject: category,
            guid,
        };
        actionHandlerCallback(e, sectionId, category.actionId, actionParam);
    }

    get sectionData() {
        const { PDPDataObject, appsPanelSkeleton } = this.props;
        return this.validateAndProcessData(PDPDataObject, appsPanelSkeleton);
    }

    getSectionGroup(categoryId) {
        const { appsPanelSkeleton } = this.props;
        return appsPanelSkeleton.sectionGroupList.find(
            section => section.appSections.find(
                inAppSection => inAppSection.id === categoryId
            )
        );
    }

    getLocalizedText(categoryId) {
        let locText;
        const sectionGroup = this.getSectionGroup(categoryId);
        if (sectionGroup) {
            sectionGroup.appSections.forEach((inAppSection) => {
                if (inAppSection.id === categoryId) {
                    const { localizedText } = inAppSection;
                    locText = localizedText;
                }
            });
        }
        return locText;
    }

    validateAndProcessData(PDPDataObject, appsPanelSkeleton) {
	    if (appsPanelSkeleton.sectionGroupList.length === 0) {
	        return null;
	    }
	    const appSectionsFilter = PDPDataObject.app.appSectionFilter;
	    const categorySectionList = appsPanelSkeleton.sectionGroupList
	        .filter(navCategoryItem => (
	            navCategoryItem.id === 'categories'));

	    const categorySection = categorySectionList.length ? categorySectionList[0] : null;

	    if (!categorySection) {
	        return null;
	    }

	    const skeletonCategories = categorySection.appSections
	        .map(appSection => appSection.id);
	    const commonCagtegoires = appSectionsFilter.filter(category => skeletonCategories
	        .includes(category));
	    const categoryList = commonCagtegoires.map(categoryId => ({
	        id: categoryId,
	        actionId: PDP_ACTION.CATEGORY_LABEL_CLICK,
	    }));
	    return { categoryList, sectionId: PDP_SECTION.CATEGORIES };
    }

    render() {
        if (this.sectionData) {
            const tagArr = [];
            const { categoryList } = this.sectionData;
            categoryList.forEach((category) => {
                const categoryId = category.id;
                if (this.getSectionGroup(categoryId)) {
                    const categoryName = this.getLocalizedText(categoryId);
                    tagArr.push(
                        <Tag
                            readOnly
                            key={categoryId}
                            tabIndex={0}
                            role="link"
                            aria-label={categoryName}
                            className="category-tag"
                            onClick={(e) => { this.onLabelClick(e, category); }}
                            onKeyDown={(e) => {
                                if (e.keyCode === 13) {
                                    this.onLabelClick(e, category);
                                }
                            }}>
                            {categoryName}
                        </Tag>
                    );
                }
            });
            if (tagArr.length > 0) {
                return (
                    <div className="categories right-panel-compt">
                        <h1 className="categories-heading">{TranslationUtil.getMessage('ccx.pdp.categories.section-name')}</h1>
                        <div className="categories-list">
                            {tagArr}
                        </div>
                    </div>
                );
            }
            return null;
        }
        return null;
    }
}

export default observer(Categories);
